/**
 * The wall cabinet with drawers (claude-docs/2026-10-30-venus-full-line-design.md,
 * group 4): an upper bay's front (`type: 'wall-drawers'`) — Venus `WD1848` /
 * `WD1854` / `WD1860`, "Drawer Wall - 1 Door & 2 Drawer - 18"W x 48"H x
 * 15"D" (catalog p.13): a door over two 9" drawers.
 *
 * The user (2026-11-01: "interesting. yes"): it STANDS ON THE COUNTER and
 * rises to the uppers' top line, 15" deep with its back on the wall
 * (`upperTier.buildUpperBox`, from the band's `counterTopY`) — so it is
 * offered only where that line is 48", 54" or 60" over the counter. A
 * generic line offers the same sizes. No light rail or under-cabinet light
 * (it has no underside over the counter), no valance, no wainscot panel.
 *
 * Pure; metres.
 */
const { findRunByIdOrAlias } = require('./runIds');

const IN = 0.0254;
const TOL_IN = 1 / 16;

const WALL_DRAWERS = Object.freeze({
  widthIn: 18,
  depthIn: 15,
  heightsIn: Object.freeze([48, 54, 60]),
  drawerIn: 9,
  drawers: 2,
});

/** Is this upper a wall cabinet with drawers? */
const isWallDrawers = (o) => o?.category?.wallDrawers === true;

const said = (m) => `${Math.round((m / IN) * 8) / 8}"`;
const listText = (xs) =>
  xs.length > 1
    ? `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`
    : xs.join('');

/** The top of the run's counter among `objects`, or null when none. */
function counterTopOf(run, objects) {
  const tops = (objects || [])
    .filter(
      (o) =>
        o?.type === 'countertop' &&
        o.center &&
        o.dimensions &&
        (!o.runId || !!findRunByIdOrAlias([run], o.runId))
    )
    .map((o) => o.center.y + o.dimensions.y / 2);
  return tops.length ? Math.max(...tops) : null;
}

/**
 * Why this upper bay cannot be a wall cabinet with drawers, or null when it
 * can: 18" wide, the uppers' top line (the bay's own top) 48", 54" or 60"
 * over the run's counter (`objects`, the room's; without a counter among
 * them the height goes unasked).
 */
function wallDrawersReason(run, cabinet, objects) {
  const d = cabinet?.dimensions;
  if (!(d?.x > 0)) return null;
  if (Math.abs(d.x / IN - WALL_DRAWERS.widthIn) > TOL_IN)
    return `A wall cabinet with drawers is ${WALL_DRAWERS.widthIn}" wide: this bay is ${said(d.x)}`;
  const counterTop = counterTopOf(run, objects);
  if (counterTop === null || !cabinet.center) return null;
  const rise = cabinet.center.y + d.y / 2 - counterTop;
  if (WALL_DRAWERS.heightsIn.some((h) => Math.abs(h * IN - rise) <= 0.5 * IN))
    return null;
  return `A wall cabinet with drawers stands on the counter, ${listText(WALL_DRAWERS.heightsIn.map((h) => `${h}"`))} tall: the uppers' top here is ${said(rise)} over it`;
}

/**
 * What the one click sets the run's uppers to (the user, 2026-11-01): their
 * bottoms the standard 18" over the counter, their height kept — an
 * `upperSpec` patch, a `null` clearing a field back to the standard.
 */
const STANDARD_HANG = Object.freeze({ anchor: null, gap: null });

/** The run with its uppers at the standard hang, or null when they hang there already. */
function atStandardHang(run) {
  const spec = run?.upperSpec || {};
  if (spec.anchor !== 'ceiling' && !(spec.gap > 0)) return null;
  const { anchor: _anchor, gap: _gap, ...rest } = spec;
  return { ...run, upperSpec: rest };
}

const inchesOf = (m) => Math.round((m / IN) * 8) / 8;

/**
 * The one click that lets this bay be a wall cabinet with drawers when the
 * uppers' height keeps it out (the user, 2026-11-01: "Hang this run's uppers
 * 18" over the counter (they're at 22"), so their top lands at 48""): the
 * run's uppers hung the standard 18" over the counter, where that puts their
 * top 48", 54" or 60" over it, and the bay set 18" wide. Every upper in the
 * run moves, so the panel says by how much before it is done.
 *
 * Null when the bay can be one as it stands, when the uppers hang at the
 * standard already, or when it would still not fit there.
 *
 * @param {Object} band  the run's upper band as it hangs
 *   (`materializeRun.runUpperBand`), with `standardHang` — the band at the
 *   standard hang (`atStandardHang`), absent when it hangs there already
 * @returns {{ upperSpec: Object, widthM: number, gapIn: number,
 *   nowGapIn: number|null, topIn: number, moveIn: number }|null} `nowGapIn`
 *   null for uppers hung from the ceiling; `moveIn` negative for down
 */
function wallDrawersHangFix(run, cabinet, objects, band) {
  const standard = band?.standardHang;
  if (!standard || !cabinet?.center || !cabinet.dimensions) return null;
  if (!wallDrawersReason(run, cabinet, objects)) return null;
  const counterTop = counterTopOf(run, objects);
  if (counterTop === null) return null;
  const moveM = standard.topY - band.topY;
  if (Math.abs(moveM) < TOL_IN * IN) return null;
  const moved = {
    ...cabinet,
    center: { ...cabinet.center, y: cabinet.center.y + moveM },
    dimensions: { ...cabinet.dimensions, x: WALL_DRAWERS.widthIn * IN },
  };
  if (wallDrawersReason(run, moved, objects)) return null;
  return {
    upperSpec: STANDARD_HANG,
    widthM: WALL_DRAWERS.widthIn * IN,
    gapIn: inchesOf(standard.bottomY - counterTop),
    nowGapIn:
      run?.upperSpec?.anchor === 'ceiling'
        ? null
        : inchesOf(band.bottomY - counterTop),
    topIn: inchesOf(moved.center.y + moved.dimensions.y / 2 - counterTop),
    moveIn: inchesOf(moveM),
  };
}

module.exports = {
  WALL_DRAWERS,
  STANDARD_HANG,
  isWallDrawers,
  wallDrawersReason,
  atStandardHang,
  wallDrawersHangFix,
};
